"use strict";

import ReactDOM from 'react-dom';
import React from 'react';

import { _I_, defineSimpleTerm } from 'components/locs';
import { PromiseDelay, decoratorRateLimit, DataSource, classMixer } from 'generics/utils';
import {  dataBound,  SourceListener  } from 'generics/dataBound';
import { BootButton, CloseButton } from 'generics/buttons';
import { IconGlyphicon, IconFontAwesome } from 'generics/icons';
import { ClassByHashnameResolver } from 'api/tradeAPICall';
import { ItemClassInfoResolver } from 'api/assetAPICall';
import { MarketCancelOrder } from 'api/marketAPICall';
import { MarketOrders, MarketTradeHistory } from 'api/ordersAPICall';

import ApplicationEvents from 'components/applicationEvents';
import MoneyUtil from 'components/moneyUtil';
import { ViewAppIdIcon, ViewItemIconic } from 'components/commonAssets';
import { PagerControl } from 'components/generics';
import  {  fakeClassInfo,  isExpired  } from 'components/classInfoUtil';
import {
    refreshBlockedPairs,  blockedPairsSource,  blockedPairsKey,
    getBlockedPairAffectAfter, isPairBlocked,
  } from 'api/rpcAPICall';
import { spawnDialogWindow } from 'components/dialogHolder';
import {  TrivialDialog  } from 'components/trivialDialog';


// LogStream.enable( "VIEWORDERSLIST" );
const loggerViewOrdersList =  LogStream("VIEWORDERSLIST");

const loggerErrors =  LogStream("VIEWORDERSLISTERROR");
LogStream.enable( "VIEWORDERSLISTERROR" );


const  ORDERS_LOAD_DELAY_MS = 300;
const  HISTORY_LOAD_DELAY_MS = 400;


export const ordersSummary = new DataSource({
    buyOrders:[],
    buyOrdersTotal:0,
    sellOrders:[],
    sellOrdersTotal:0,
  });


function reducerOrdersTotal( accum, order )  {
  const  {  normalPrice,  localPrice,  currency,  amount,  doneAmount,  } = order;
  const price = MoneyUtil.preferMoney(normalPrice, localPrice, currency, currency);
  return accum + (price * (amount||0))
}


const  ordersByTime = (l,r) => (r.time - l.time);


function setOrders(orders)  {

  const buyOrders = orders.filter( it => (it.type == 'BUY') ).sort(ordersByTime);
  const buyOrdersTotal = buyOrders.reduce( reducerOrdersTotal, 0 );

  const sellOrders = orders.filter( it => (it.type == 'SELL') ).sort(ordersByTime);
  const sellOrdersTotal = sellOrders.reduce( reducerOrdersTotal, 0 );

  ordersSummary.update({
      buyOrders,
      buyOrdersTotal,
      sellOrders,
      sellOrdersTotal
    });
}


ordersSummary.on(orders  =>  {
    loggerViewOrdersList.debug( "ordersSummary(", orders, ")" );
  });


const reloadOrdersDelayed = decoratorRateLimit(
  ORDERS_LOAD_DELAY_MS

)(() => {
  return MarketOrders(

    ).catch(  (reason) =>  {
      reason = Object.assign({},  reason,  {"caption": "Failed to load own orders data:"});
      ApplicationEvents.emit_async( "EventLog.append", reason );
      return [];

    }).then( (resp) =>  {
      loggerViewOrdersList.info( "reloadOrdersDelayed -> ", resp );
      setOrders(resp||[]);
    });
});


// {
//   "eventType":"deal",
//   "appId":"1067",
//   "hashname":"\"Chrysanthemum\" emblem",
//   "buyerUserId":"4208939",
//   "sellerUserId":"492181"
// }
function onTickerEvent(args, eventParams, details)  {
  const topic = details.decodedTopic;
  if ( !topic )  {
    return;
  }
  if ( (topic.eventType != "book") && (topic.eventType != "deal") )  {
    return;
  }
  const ownerUserId = `${SettingsInjections.getCredentials().userId}`;
  if ( (topic.buyerUserId != ownerUserId) && (topic.sellerUserId != ownerUserId) )  {
    return;
  }

  loggerViewOrdersList.info( `onTickerEvent update due to ${details.topic}` );
  ApplicationEvents.emit_async("market.ordersUpdate");
}

ApplicationEvents.on("market.ordersUpdate", reloadOrdersDelayed );
ApplicationEvents.on("market.notify.received", onTickerEvent );


export const ordersHistory = new DataSource({
    list:[],
    stage:"dirty",
  });


const  historyByTime = (l,r) => (r.doneTime&&l.doneTime)?(r.doneTime - l.doneTime):(r.time - l.time);


function setHistory(history, stage)  {
  const changed = {  stage  };
  if ( history )
    changed.list = history.sort(historyByTime)
  ordersHistory.update(changed);
}


ordersHistory.on(history  =>  {
    loggerViewOrdersList.debug( "ordersHistory(", history, ")" );
  });


function reloadHistory() {
  setHistory(null, "pending");
  return MarketTradeHistory().then(  (resp) =>  {
      loggerViewOrdersList.info( "loadOrdersHistory -> ", resp );
      setHistory([ ...resp ], "done");

    }).catch(  (reason) =>  {
      reason = Object.assign({},  reason,  {  "caption": "Failed to load orders history data:",  });
      ApplicationEvents.emit_async( "EventLog.append", reason );
      setHistory([], "fail");

    });
}


function spawnCancelOrderModal( order )  {
  spawnDialogWindow(ViewCancelOrderItemDialog,  {
      order
    });
}


function viewOrderItem( order )  {
  ApplicationEvents.emit_async( "market.setCurrentAssetByHash", order.appId, order.market );
}


export class ViewOrdersListContainer extends React.Component {
  constructor(props)  {
    super(props);

    const creds  =  SettingsInjections.getCredentials() || {};
    const limitedAccount = (creds.need2step && (!creds.have2step)) ||
                           (creds.needEmailVerification && (!creds.haveEmailVerification));
    const market = SettingsInjections.getUsedCurrency().market;

    this.state = {
      "activeTab":"sellOrders",
      limitedAccount,
      market,
    };
  }


  componentDidMount()  {
    ApplicationEvents.on("market.ordersUpdate", this.handleUpdate ); // mark history dirty
    reloadOrdersDelayed();
  }


  componentWillUnmount()  {
    ApplicationEvents.off("market.ordersUpdate", this.handleUpdate );
  }


  handleUpdate = ()  =>  {
    setHistory(null, "dirty");

    const  {  activeTab  }  =  this.state;
    if (activeTab == "historyList")
      PromiseDelay(  HISTORY_LOAD_DELAY_MS  ).then(  reloadHistory  );
  }


  handleSwitchTab = ( activeTab )  =>  {
    if (activeTab == "historyList")  {
      const {  stage  }  =  ordersHistory.get();
      if ((stage == "dirty") || (stage == "fail"))  {
        reloadHistory();
      }
    }
    this.setState({ activeTab });
  }


  render() {
    const  {  limitedAccount, activeTab,  market  }  =  this.state;

    return (<SourceListener orders={ordersSummary} history={ordersHistory} >{
      ({orders, history}) => {
        const  {  sellOrders,  sellOrdersTotal,  buyOrders,  buyOrdersTotal  }  =  orders;

        return (
            <div>
              <ul className="nav nav-tabs" role="tablist">
                <ViewOrdersListTabHeading tabName="sellOrders" activeTab={activeTab}
                                          setActive={this.handleSwitchTab}  >
                  <ViewTabAmountPad  orders={sellOrders}  total={sellOrdersTotal}  market={market}  />
                </ViewOrdersListTabHeading>

                <ViewOrdersListTabHeading tabName="buyOrders" activeTab={activeTab}
                                          setActive={this.handleSwitchTab}  >
                  <ViewTabAmountPad  orders={buyOrders}  total={buyOrdersTotal}  market={market}  />
                </ViewOrdersListTabHeading>

                <ViewOrdersListTabHeading tabName="historyList" activeTab={activeTab}
                                          setActive={this.handleSwitchTab}  >
                </ViewOrdersListTabHeading>

              </ul>

              <div className="tab-content">
                <ViewOrdersListTab  orderType="buy" orders={buyOrders}
                                    active={activeTab == "buyOrders"}
                                    limitedAccount={limitedAccount} />

                <ViewOrdersListTab  orderType="sell" orders={sellOrders}
                                    active={activeTab == "sellOrders"}
                                    limitedAccount={limitedAccount} />

                <ViewOrdersListTab  orderType="history" orders={history.list}
                                    active={activeTab == "historyList"}
                                    limitedAccount={limitedAccount} />

              </div>
            </div>
          );
      }}</SourceListener>
    );
  }
}


function ViewOrdersListTabHeading({  tabName, activeTab, setActive, children  })  {
  const tabActive = classMixer((activeTab == tabName) && "active");
  return (
      <li   role="button"  className={tabActive}
            onClick={()  =>  setActive(tabName)}  >
        <a role="tab" >
          {_I_(`OrdersListContainer.Tab.${tabName}`)}
          {" "}
          {children}
        </a>
      </li>
    );
}


function ViewTabAmountPad({  total,  orders,  market  })  {
    return (
        <span className="tab-amount-pad">
          <i>
            {!!total && (MoneyUtil.formatMoney(total, market)+" / ")}
            {orders.length}
          </i>
        </span>);
}


function  OrderEntryBasicInfo({ order, blockedAfter })  {
  const orderStamp = new Date( order.time*1000 ).toLocaleString();
  const orderDoneStamp = !!order.doneTime && new Date( order.doneTime*1000 ).toLocaleString();
  const haveBlockSchedule = blockedAfter != 0;
  const pairIsBlocked = isPairBlocked(blockedAfter);
  return (
      <div className="view-order-entry-info">
        <div className="entry-order">
          {_I_("ViewOrdersEntry.order.order")} #{order.id}{!!order.mid && `/${order.mid}`}
        </div>
        <div className="entry-stamp">{_I_("ViewOrdersEntry.order.issued")} {orderStamp}</div>
        {!!orderDoneStamp &&
            <div className="entry-stamp">{_I_("ViewOrdersEntry.order.executed")} {orderDoneStamp}</div>}
        <div className="entry-type">{_I_(`ViewOrdersEntry.order.${order.type}`)}</div>
        {haveBlockSchedule&&(
          pairIsBlocked?
              (<div className="entry-blocked">{_I_(`ViewOrdersEntry.order.pairIsBlocked`)}</div>):
              (<div className="entry-blocked">
                  {_I_(`ViewOrdersEntry.order.pairWillBlockAfter`)} {new Date(blockedAfter).toLocaleString()}
                </div>)
          )}
      </div>
    );
}


function OrderEntryNotes({ order })  {
  const  {  currency,  amount,  doneAmount  }  =  order;

  const price = MoneyUtil.preferMoney(order.normalPrice, order.localPrice, currency, currency);
  const orderPrice = MoneyUtil.formatMoney(price, currency);

  const orderSum = MoneyUtil.formatMoney(price * amount, currency);
  const orderDoneSum = MoneyUtil.formatMoney(price * doneAmount, currency);

  return (
      <div className="view-order-entry-notes">

        {(amount > 0)  &&
            <div key="amount" className="view-order-amount">
              <div>{_I_("ViewOrdersEntry.amounts.remain")}</div>
              <div>{amount}&nbsp;&times;&nbsp;{orderPrice}</div>
              <div className="view-order-sum">{orderSum}</div>
            </div>}

        {(doneAmount > 0)  &&
            <div key="doneAmount" className="view-order-amount">
              <div>{_I_("ViewOrdersEntry.amounts.done")}</div>
              <div>{doneAmount}&nbsp;&times;&nbsp;{orderPrice}</div>
              <div className="view-order-sum">{orderDoneSum}</div>
            </div>}

      </div>
    );
}


function OrderEntryIcon({ order, classInfo, blockedAfter })  {
  const expired = isExpired(classInfo);

  const pairIsBlocked = isPairBlocked(blockedAfter);

  const canView =  (!expired && classInfo.marketable && !pairIsBlocked);
  const handleView = canView ? () => viewOrderItem( order ) : undefined;

  return (<ViewItemIconic classInfo={classInfo} onClick={handleView} />);
}


function OrderEntryActions({ order, classInfo, orderType, limitedAccount, blockedAfter })  {
  const expired = isExpired(classInfo);
  const pairIsBlocked = isPairBlocked(blockedAfter);

  const canView =  (!expired && classInfo.marketable);
  const handleView = canView ? () => viewOrderItem( order ) : undefined;

  const canCancel =  (!expired && !pairIsBlocked && !limitedAccount &&
                      (orderType != "history") &&
                      (classInfo.marketable || classInfo.IS_UNRESOLVED));
  const handleCancel = canCancel ? () => spawnCancelOrderModal( order ) : undefined;

  return (
      <div className="view-order-entry-buttons">
        {(!!handleView)  &&
          <BootButton size="small"  variant="success"
                      onClick={handleView}
              >{_I_("ViewOrdersEntry.order.view")}</BootButton>
          }
        {(!!handleCancel) &&
          <BootButton size="small"  variant="warning"
                      onClick={handleCancel}
              >{_I_("ViewOrdersEntry.order.cancel")}</BootButton>
          }
      </div>
    );
}


class ViewOrdersEntry extends React.Component {
  constructor(props)  {
    super(props);

    const { appId, market:marketName } = this.props.order;
    const pairKey = blockedPairsKey({appId,marketName});
    this.state = { "classInfo" : fakeClassInfo("pending", marketName), pairKey };
  }


  resolveOrderClass( order )  {
    const appId = order.appId;
    const marketName = order.market;
    const pairKey = blockedPairsKey({appId,marketName});

    refreshBlockedPairs(appId, marketName);

    ClassByHashnameResolver.resolve(
        appId, marketName
      ).then(
        (assetclass) =>  ItemClassInfoResolver.resolve( appId, assetclass )
      ).then(
        ( classInfo ) =>  this.setState({ classInfo, pairKey })
      ).catch(  // it's possible to have outdated orders in history
        ( reason ) =>  {
          this.setState({ "classInfo" : fakeClassInfo( "broken", marketName ), pairKey })
          loggerErrors.error( "ViewOrdersEntry#resolveOrderClass(", appId, marketName,") - Failed (", reason, ")" );
        }
      );
   }


  componentWillMount() {
    this.resolveOrderClass( this.props.order );
  }


  componentWillReceiveProps(nextProps) {
    if ( this.props.order == nextProps.order )
      return;

    this.resolveOrderClass( nextProps.order )
  }


  render()  {
    const {  classInfo, pairKey  } = this.state;
    const {  order,  orderType,  limitedAccount  } = this.props;
    
    const nameTextColor = classInfo.name_color ? {"color":'#'+classInfo.name_color} : {};

    return (<SourceListener blockedPairs={blockedPairsSource}>{
      ({blockedPairs})  =>  {
          const blockedAfter = getBlockedPairAffectAfter(blockedPairs[pairKey]);
          return (
              <div  className="view-order-entry" >
                <OrderEntryIcon classInfo={classInfo} order={order}
                      blockedAfter={blockedAfter} />
                <div className="view-order-entry-body">
                  <div className="view-order-entry-header">
                    <div className="view-order-entry-title" style={nameTextColor}>{classInfo.name}</div>
                    <ViewAppIdIcon appId={order.appId} compact />
                  </div>
                  <div className="view-order-entry-details">
                    <OrderEntryBasicInfo order={order}
                        blockedAfter={blockedAfter} />
                    <OrderEntryNotes order={order} />
                    <OrderEntryActions order={order} classInfo={classInfo}
                        limitedAccount={limitedAccount} orderType={orderType}
                        blockedAfter={blockedAfter} />
                  </div>
                </div>
              </div>
            );

        }
    }</SourceListener>
      );
  }


}


class ViewOrdersListTab extends React.Component {
  constructor(props)  {
    super(props);

    this.state = { "pageShift" : 0 };
  }


  renderNoOrders()  {
    const { active } = this.props;
    const tabActive = classMixer( "tab-pane", active&&"active" );
    return (
        <div  role="tabpanel"  className={tabActive} >
          <div  className="view-orders-pane">
            <div className="text-center">
              <IconGlyphicon type="horizontal"/>&nbsp;{_I_("ViewOrdersList.empty.label")}
            </div>
          </div>
        </div>
      );
  }


  render()
  {
    const { orders, orderType, active } = this.props;
    if ( !orders.length )
      return this.renderNoOrders();

    const pageSize = 18;
    const pageShift = (this.state.pageShift < orders.length) ? this.state.pageShift : 0;
    const pageOrders = orders.slice( pageShift, pageShift+pageSize );

    const tabActive = classMixer( "tab-pane", active&&"active" );

    return (
      <div  role="tabpanel"  className={tabActive} >
        <div  className="view-orders-pane">
            {
              pageOrders.map( (order) =>  (<ViewOrdersEntry
                                                key={order.id}
                                                order={order}
                                                orderType={orderType}
                                                limitedAccount={this.props.limitedAccount}
                                              />) )
            }
        </div>

        <PagerControl
              pageShift={pageShift}
              setPageShift={(ps) =>  this.setState({ "pageShift":ps })}
              pageSize={pageSize}
              itemsCount={orders.length}
            />

      </div>
    )
  }
}


class ViewCancelOrderItemDialog extends React.Component  {
  constructor(props)  {
    super(props);
  }


  doCancelOrder = () =>  {
    spawnCancelOrderItemProcessingModal([this.props.order]);
    this.props.removeModal();
  };


  doCancelAllOrder = ( orders ) =>  {
    const  { sellOrders, buyOrders }  =  orders;
    const activeTab = this.props.order.type;
    let cancelOrders = [];

    const ordersSummaryType = (activeTab == "SELL") ? sellOrders : buyOrders;

    ordersSummaryType.forEach ((order) => {
      if (order.market == this.props.order.market) {
        cancelOrders.push (order);
      };
    });

    spawnCancelOrderItemProcessingModal (cancelOrders);
    this.props.removeModal();
  };

  onBackdropTouch()  {
    this.props.removeModal();
  }


  render()  {
    return (
        <div className="panel panel-info panel-sm view-cancel-order-item-dialog">
          <div className="panel-heading">
            {_I_("CancelOrderDialog.heading.youSure")}
            <CloseButton onClick={this.props.removeModal} />
          </div>

          <div className="panel-body">
            <div className="text-center">
              <h1>
                <IconGlyphicon type="question-mark"/>
              </h1>
              <b>{_I_("CancelOrderDialog.body.youSure")}</b>
              <div className="view-confirm-dialog-buttons">
                <BootButton variant="success"
                            onClick={this.doCancelOrder}
                      >
                  <IconFontAwesome type="check" />&nbsp;
                  {_I_("CancelOrderDialog.button.cancel")}
                </BootButton>

                <SourceListener orders={ordersSummary}>{
                  ({orders}) => {
                    return (
                        <BootButton variant="success"
                                    onClick={() => this.doCancelAllOrder(orders)}>
                          <IconFontAwesome type="check" />&nbsp;
                          {_I_("CancelAllOrderDialog.button.cancel")}
                        </BootButton>
                    );
                  }
                }</SourceListener>

                <BootButton  variant="warning"
                             onClick={this.props.removeModal}
                      >
                  <IconFontAwesome type="remove" />&nbsp;{_I_("CancelOrderDialog.button.noCancel")}
                </BootButton>

              </div>
            </div>
          </div>
        </div>
      );
  }
};


class ViewCancelOrderItemProcessingDialog extends React.Component  {
  constructor(props)  {
    super(props);
  }


  onBackdropTouch()  {
    // this.props.removeModal();  //  Cannot be closed
  }


  componentDidMount()  {
    const orders = this.props.order;

      Promise.all(  orders.map(
          ( order ) => MarketCancelOrder(
              order.pairId, order.id
          )
      ) ).then((resp) => {
        loggerViewOrdersList.info("cancelOrderItem -> ", resp);
      }).catch((reason) => {
        loggerErrors.error("cancelOrderItem - Failed (", reason, ")");
        spawnCancelOrderErrorModal();
      }).then(() => {
        ApplicationEvents.emit_async("market.updateInventory");
        ApplicationEvents.emit_async("market.ordersUpdate");
      }).then(() => {
        this.props.removeModal();
      });
  }


  render()  {
    return (
        <div className="panel panel-info panel-sm view-is-processing-dialog">
          <div className="panel-heading">
            {_I_("CancelOrderDialog.heading.processing")}
          </div>

          <div className="panel-body">
            <div className="text-center">
              <IconFontAwesome type="spinner" size="3x" pulse fixedWidth />&nbsp;
            </div>
            <div className="text-center">
              <b>{_I_("CancelOrderDialog.body.processing")}</b>
            </div>
          </div>
        </div>
      );
  }
};


function spawnCancelOrderItemProcessingModal( order )  {
  spawnDialogWindow(ViewCancelOrderItemProcessingDialog,  {
      order
    });
}


class ViewCancelOrderErrorDialog extends React.Component  {
  onBackdropTouch()  {
    this.props.removeModal();
  }


  render()  {
    const  { removeModal  } = this.props;
    return (<TrivialDialog
                prefixL10N={`CancelOrderErrorModal`}
                panelType="warning"
                onCloseButton={removeModal}
                onCancelButton={removeModal}
                bodyIcon={<IconFontAwesome type="exclamation-triangle" size="3x" fixedWidth />}
                />);
  }
};


function spawnCancelOrderErrorModal()  {
  spawnDialogWindow(ViewCancelOrderErrorDialog,  {});
}

defineSimpleTerm("CancelOrderErrorModal.heading",  "Error",  {
  "ru_RU": "Ошибка",
  "cs_CZ": "Error",
  "fr_FR": "Erreur",
  "de_DE": "Fehler",
  "it_IT": "Errore",
  "ko_KR": "오류",
  "pl_PL": "Błąd",
  "pt_PT": "Error",
  "es_ES": "Error",
  "zh_CN": "错误",
  "zh_TW": "錯誤"
} );

defineSimpleTerm("CancelOrderErrorModal.prompt",  "Failed to cancel order",  {
  "ru_RU": "Возникла ошибка при отмене",
  "cs_CZ": "Failed to cancel order",
  "fr_FR": "Echec de l'émission de l'ordre",
  "de_DE": "Stornierung der Bestellung abgebrochen",
  "it_IT": "Cancellazione ordine fallita",
  "ko_KR": "주문 취소에 실패하였습니다",
  "pl_PL": "Nie udało się anulować polecenia",
  "pt_PT": "Failed to cancel order",
  "es_ES": "No se pudo cancelar el pedido",
  "zh_CN": "取消订单失败",
  "zh_TW": "取消訂單失敗"
} );

defineSimpleTerm("CancelOrderErrorModal.explanation",  "Possible reasons are network problems or you were at the time of the order update.\nTry again a bit later.",  {
  "ru_RU": "Это могло произойти из-за проблем с сетью или вы попали на момент обновления ордера.\nПопробуйте повторить операцию немного позднее.",
  "cs_CZ": "Possible reasons are network problems or you were at the time of the order update.\nTry again a bit later.",
  "fr_FR": "Les raisons possibles sont des problèmes de réseau ou une mise à jour durant la commande. \nRéessayez un peu plus tard.",
  "de_DE": "Mögliche Gründe sind Netzwerkprobleme oder du hast während der Bestellung aktualisiert.\nBitte etwas später erneut probieren.",
  "it_IT": "Potrebbe essere un problema di connessione o di aggiornamento dell'ordine.\nRiprova tra un po'.",
  "ko_KR": "네트워크 문제 혹은 주문 업데이트로 인해 작업에 실패하였습니다. \n잠시 후 다시 시도해 주십시오",
  "pl_PL": "Możliwe przyczyny to problemy sieciowe albo sprawdzenie w czasie odświeżania polecenia.\nSpróbuj ponownie za chwilę.",
  "pt_PT": "Possible reasons are network problems or you were at the time of the order update.\nTry again a bit later.",
  "es_ES": "Los posibles motivos son problemas de red o estabas en el momento de la actualización del pedido.\n Inténtalo de nuevo un poco más tarde.",
  "zh_CN": "可能的原因是网络问题，或你正好遇到订单更新。\n请稍后再试。",
  "zh_TW": "可能的原因是網路問題，或你正好遇到訂單更新。\n請稍後再試。"
} );

// defineSimpleTerm("CancelOrderErrorModal.okButton",  "",  {
//   "ru_RU": "",
// } );

// defineSimpleTerm("CancelOrderErrorModal.defaultButton",  "",  {
//   "ru_RU": "",
// } );

defineSimpleTerm("CancelOrderErrorModal.cancelButton",  "Got it",  {
  "ru_RU": "Окей",
  "cs_CZ": "Got it",
  "fr_FR": "Je l'ai eu",
  "de_DE": "Verstanden",
  "it_IT": "Preso",
  "ko_KR": "확인",
  "pl_PL": "Mam to",
  "pt_PT": "Got it",
  "es_ES": "Lo tengo",
  "zh_CN": "了解",
  "zh_TW": "了解"
} );


defineSimpleTerm( "OrdersListContainer.Tab.sellOrders",  "Sell orders",       {
  "ru_RU": "Ордера на продажу",
  "cs_CZ": "Sell orders",
  "fr_FR": "Ordres de vente",
  "de_DE": "Aufträge verkaufen",
  "it_IT": "Ordini di vendita",
  "ko_KR": "판매 주문",
  "pl_PL": "Polecenia sprzedaży",
  "pt_PT": "Sell orders",
  "es_ES": "Pedidos de venta",
  "zh_CN": "出售订单",
  "zh_TW": "出售訂單"
} );

defineSimpleTerm( "OrdersListContainer.Tab.buyOrders",  "Buy orders",       {
  "ru_RU": "Ордера на покупку",
  "cs_CZ": "Buy orders",
  "fr_FR": "Ordres d'achat",
  "de_DE": "Aufträge kaufen",
  "it_IT": "Ordini d'acquisto",
  "ko_KR": "구매 주문",
  "pl_PL": "Polecenia kupna",
  "pt_PT": "Buy orders",
  "es_ES": "Pedidos de compra",
  "zh_CN": "购买订单",
  "zh_TW": "購買訂單"
} );

defineSimpleTerm( "OrdersListContainer.Tab.historyList",  "Orders history",       {
  "ru_RU": "История ордеров",
  "cs_CZ": "Orders history",
  "fr_FR": "Historique des commandes",
  "de_DE": "Auftragsverlauf",
  "it_IT": "Storico ordini",
  "ko_KR": "주문 이력",
  "pl_PL": "Historia zakupów",
  "pt_PT": "Orders history",
  "es_ES": "Historial de pedidos",
  "zh_CN": "订单历史",
  "zh_TW": "訂單歷史"
} );


defineSimpleTerm( "ViewOrdersEntry.amounts.remain",  "Remain",       {
  "ru_RU": "Остаток",
  "cs_CZ": "Remain",
  "fr_FR": "Reste",
  "de_DE": "Verblieben",
  "it_IT": "Restanti",
  "ko_KR": "재고",
  "pl_PL": "Pozostało",
  "pt_PT": "Remain",
  "es_ES": "Quedan",
  "zh_CN": "剩余",
  "zh_TW": "剩餘"
} );

defineSimpleTerm( "ViewOrdersEntry.amounts.done",  "Done",       {
  "ru_RU": "Завершено",
  "cs_CZ": "Done",
  "fr_FR": "Terminé",
  "de_DE": "Fertig",
  "it_IT": "Fatto",
  "ko_KR": "처리됨",
  "pl_PL": "Gotowe",
  "pt_PT": "Done",
  "es_ES": "Listo",
  "zh_CN": "完成",
  "zh_TW": "完成"
} );

defineSimpleTerm( "ViewOrdersEntry.order.order",  "Order",       {
  "ru_RU": "Ордер",
  "cs_CZ": "Order",
  "fr_FR": "Ordre",
  "de_DE": "Auftrag",
  "it_IT": "Ordine",
  "ko_KR": "주문",
  "pl_PL": "Zamów",
  "pt_PT": "Order",
  "es_ES": "Orden",
  "zh_CN": "订单",
  "zh_TW": "訂單"
} );

defineSimpleTerm( "ViewOrdersEntry.order.issued",  "Issued",       {
  "ru_RU": "От",
  "cs_CZ": "Issued",
  "fr_FR": "Emis",
  "de_DE": "Ausgegeben",
  "it_IT": "Emesso",
  "ko_KR": "발행됨",
  "pl_PL": "Wystawione",
  "pt_PT": "Issued",
  "es_ES": "Emitido",
  "zh_CN": "已提出",
  "zh_TW": "已提出"
} );

defineSimpleTerm( "ViewOrdersEntry.order.executed",  "Executed",       {
  "ru_RU": "Исполнен",
  "cs_CZ": "Executed",
  "fr_FR": "Réalisé",
  "de_DE": "Ausgeführt",
  "it_IT": "Eseguito",
  "ko_KR": "처리됨",
  "pl_PL": "Wykonane",
  "pt_PT": "Executed",
  "es_ES": "Ejecutado",
  "zh_CN": "已执行",
  "zh_TW": "已執行"
} );

defineSimpleTerm( "ViewOrdersEntry.order.pairIsBlocked",  "Trade locked",       {
  "ru_RU": "Торговля остановлена",
  "cs_CZ": "Trade locked",
  "fr_FR": "Marché fermé",
  "de_DE": "Handel gesperrt",
  "it_IT": "Scambio bloccato",
  "ko_KR": "거래 잠김",
  "pl_PL": "Wymiana zablokowana",
  "pt_PT": "Trade locked",
  "es_ES": "Negocio bloqueado",
  "zh_CN": "交易已锁定",
  "zh_TW": "交易已鎖定"
} );

defineSimpleTerm( "ViewOrdersEntry.order.pairWillBlockAfter",  "Will lock at",       {
  "ru_RU": "Торговля остановится",
  "cs_CZ": "Will lock at",
  "fr_FR": "Sera fermé à",
  "de_DE": "Wird gesperrt",
  "it_IT": "Verrà bloccato tra",
  "ko_KR": "다음에 잠금될 것입니다",
  "pl_PL": "Zostanie zablokowane za",
  "pt_PT": "Will lock at",
  "es_ES": "Se bloqueara en",
  "zh_CN": "将在此后锁定：",
  "zh_TW": "將在之後鎖定："
} );

defineSimpleTerm( "ViewOrdersEntry.order.BUY",  "BUY",       {
  "ru_RU": "Покупка",
  "cs_CZ": "BUY",
  "fr_FR": "ACHETER",
  "de_DE": "KAUFEN",
  "it_IT": "COMPRA",
  "ko_KR": "구매",
  "pl_PL": "KUP",
  "pt_PT": "BUY",
  "es_ES": "COMPRAR",
  "zh_CN": "购买",
  "zh_TW": "購買"
} );

defineSimpleTerm( "ViewOrdersEntry.order.SELL",  "SELL",       {
  "ru_RU": "Продажа",
  "cs_CZ": "SELL",
  "fr_FR": "VENDRE",
  "de_DE": "VERKAUFEN",
  "it_IT": "VENDI",
  "ko_KR": "판매",
  "pl_PL": "SPRZEDAJ",
  "pt_PT": "SELL",
  "es_ES": "VENDER",
  "zh_CN": "出售",
  "zh_TW": "出售"
} );

defineSimpleTerm( "ViewOrdersEntry.order.view",  "View",       {
  "ru_RU": "Открыть",
  "cs_CZ": "View",
  "fr_FR": "Vue",
  "de_DE": "Ansicht",
  "it_IT": "Guarda",
  "ko_KR": "확인하기",
  "pl_PL": "Zobacz",
  "pt_PT": "View",
  "es_ES": "Ver",
  "zh_CN": "查看",
  "zh_TW": "查看"
} );

defineSimpleTerm( "ViewOrdersEntry.order.cancel",  "Cancel",       {
  "ru_RU": "Отменить",
  "cs_CZ": "Cancel",
  "fr_FR": "Annuler",
  "de_DE": "Abbrechen",
  "it_IT": "Annulla",
  "ko_KR": "취소",
  "pl_PL": "Anuluj",
  "pt_PT": "Cancel",
  "es_ES": "Cancelar",
  "zh_CN": "取消",
  "zh_TW": "取消"
} );

defineSimpleTerm( "ViewOrdersList.empty.label",  "Nothing here",       {
  "ru_RU": "Тут нет ничего",
  "cs_CZ": "Nothing here",
  "fr_FR": "Aucun élément",
  "de_DE": "Hier ist nichts",
  "it_IT": "Non c'è nulla",
  "ko_KR": "아무것도 없습니다",
  "pl_PL": "Nic tu nie ma",
  "pt_PT": "Nothing here",
  "es_ES": "Nada aquí",
  "zh_CN": "此处无内容",
  "zh_TW": "此處無內容"
} );

defineSimpleTerm( "CancelOrderDialog.heading.youSure",  "Are you sure?",       {
  "ru_RU": "Уверены?",
  "cs_CZ": "Are you sure?",
  "fr_FR": "Êtes-vous sûrs ?",
  "de_DE": "Bist du sicher?",
  "it_IT": "Sei sicuro?",
  "ko_KR": "결정에 확실하십니까?",
  "pl_PL": "Jesteś pewien?",
  "pt_PT": "Are you sure?",
  "es_ES": "¿Estás seguro?",
  "zh_CN": "是否确定？",
  "zh_TW": "是否確定？"
} );

defineSimpleTerm( "CancelOrderDialog.body.youSure",  "Are you sure to cancel order?",       {
  "ru_RU": "Вы уверены что хотите отменить ордер?",
  "cs_CZ": "Are you sure to cancel order?",
  "fr_FR": "Êtes-vous sûr de vouloir annuler votre commande?",
  "de_DE": "Bist du sicher den Auftrag abzubrechen?",
  "it_IT": "Sei sicuro di voler annullare l'ordine?",
  "ko_KR": "구매 주문을 취소하시겠습니까?",
  "pl_PL": "Czy na pewno chcesz anulować polecenie?",
  "pt_PT": "Are you sure to cancel order?",
  "es_ES": "¿Estás seguro de cancelar el pedido?",
  "zh_CN": "是否确定取消订单？",
  "zh_TW": "是否確定取消訂單？"
} );

defineSimpleTerm( "CancelOrderDialog.button.cancel",  "Yes, I want to cancel order",       {
  "ru_RU": "Да, отменить ордер",
  "cs_CZ": "Yes, I want to cancel order",
  "fr_FR": "Oui, je veux annuler ma commande",
  "de_DE": "Ja, du möchtest den Auftrag abbrechen",
  "it_IT": "Si, voglio annullare l'ordine",
  "ko_KR": "네, 구매 주문을 취소하겠습니다",
  "pl_PL": "Tak, chcę anulować polecenie",
  "pt_PT": "Yes, I want to cancel order",
  "es_ES": "Sí, quiero cancelar el pedido",
  "zh_CN": "是的，我要取消订单",
  "zh_TW": "是的，我要取消訂單"
} );

defineSimpleTerm("CancelAllOrderDialog.button.cancel",  "Yes for everyone",  {
  "ru_RU": "Да, для всех",
  "zh_CN": "是且不再确认"
} );

defineSimpleTerm( "CancelOrderDialog.button.noCancel",  "No",       {
  "ru_RU": "Нет",
  "cs_CZ": "No",
  "fr_FR": "Non",
  "de_DE": "Nein",
  "it_IT": "No",
  "ko_KR": "아니오",
  "pl_PL": "Nie",
  "pt_PT": "No",
  "es_ES": "No",
  "zh_CN": "否",
  "zh_TW": "否"
} );

defineSimpleTerm( "CancelOrderDialog.heading.processing",  "Processing...",       {
  "ru_RU": "Выполняется...",
  "cs_CZ": "Processing...",
  "fr_FR": "En cours...",
  "de_DE": "Bearbeiten...",
  "it_IT": "In processo...",
  "ko_KR": "진행중...",
  "pl_PL": "Przetwarzanie...",
  "pt_PT": "Processing...",
  "es_ES": "Procesando...",
  "zh_CN": "处理中…",
  "zh_TW": "處理中..."
} );

defineSimpleTerm( "CancelOrderDialog.body.processing",  "Processing, please wait",       {
  "ru_RU": "Пожалуйста подождите",
  "cs_CZ": "Processing, please wait",
  "fr_FR": "En cours, veuillez patienter",
  "de_DE": "Bearbeiten, bitte warten",
  "it_IT": "In processo, attendere",
  "ko_KR": "진행중, 잠시만 기다려 주세요",
  "pl_PL": "Przetwarzanie, prosimy czekać",
  "pt_PT": "Processing, please wait",
  "es_ES": "Procesando, por favor espera",
  "zh_CN": "处理中，请稍候",
  "zh_TW": "處理中，請稍候"
} );

